import { useOutletContext } from 'react-router';
import { useReportSummary } from '../api/reports';
import { LastUpdated } from '../components/LastUpdated';
import { Money } from '../components/Money';
import { SummaryCard } from '../components/SummaryCard';
import type { SalesContext } from './Sales';

export function SalesOverview() {
  const { eventId, range } = useOutletContext<SalesContext>();
  const { data, isLoading, isError, dataUpdatedAt } = useReportSummary(eventId, range);

  if (isLoading) return <div style={{ color: 'var(--fg-3)' }}>Loading…</div>;
  if (isError || !data) return <div className="form-error">Couldn't load summary.</div>;

  const totalSucceeded =
    data.tenderSplit.card_present.count +
    data.tenderSplit.keyed.count +
    data.tenderSplit.cash.count;
  const cpPct = totalSucceeded > 0 ? (data.tenderSplit.card_present.count / totalSucceeded) * 100 : 0;
  const keyedPct = totalSucceeded > 0 ? (data.tenderSplit.keyed.count / totalSucceeded) * 100 : 0;
  const cashPct = totalSucceeded > 0 ? (data.tenderSplit.cash.count / totalSucceeded) * 100 : 0;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {data.disputeCount > 0 && (
        <div
          className="card card-pad"
          style={{
            borderLeft: '3px solid var(--oeg-warning, #B8860B)',
            background: 'var(--bg-surface-1)',
            color: 'var(--fg-1)',
          }}
        >
          <strong>{data.disputeCount} disputed transaction{data.disputeCount > 1 ? 's' : ''}</strong>{' '}
          totaling <Money value={data.disputedAmountCents / 100} /> in this window.
        </div>
      )}

      <div className="grid-4">
        <SummaryCard
          label="Gross"
          value={<Money value={data.grossCents / 100} />}
          hint={`${data.txnCount} successful sale${data.txnCount === 1 ? '' : 's'}`}
        />
        <SummaryCard
          label="Refunds"
          value={<Money value={data.refundedCents / 100} />}
        />
        <SummaryCard
          label="Net"
          value={<Money value={data.netCents / 100} />}
          hint="Gross − refunds"
        />
        <SummaryCard
          label="Avg sale"
          value={<Money value={data.averageSaleCents / 100} />}
        />
      </div>

      <div className="card card-pad">
        <h3 style={{ margin: 0, marginBottom: 12 }}>Tender</h3>
        {totalSucceeded === 0 ? (
          <div style={{ color: 'var(--fg-3)' }}>No completed sales in this window.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            <TenderBar
              label="Tap-to-pay"
              count={data.tenderSplit.card_present.count}
              pct={cpPct}
              gross={data.tenderSplit.card_present.grossCents}
              color="var(--oeg-purple)"
            />
            <TenderBar
              label="Keyed"
              count={data.tenderSplit.keyed.count}
              pct={keyedPct}
              gross={data.tenderSplit.keyed.grossCents}
              color="var(--oeg-coral)"
            />
            <TenderBar
              label="Cash"
              count={data.tenderSplit.cash.count}
              pct={cashPct}
              gross={data.tenderSplit.cash.grossCents}
              color="var(--oeg-teal, #4FB7A0)"
            />
          </div>
        )}
      </div>

      {Object.keys(data.cardBrandSplit).length > 0 && (
        <div className="card card-pad">
          <h3 style={{ margin: 0, marginBottom: 12 }}>Card brands</h3>
          <table className="tbl">
            <thead>
              <tr>
                <th>Brand</th>
                <th className="num">Sales</th>
                <th className="num">Gross</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.cardBrandSplit)
                .sort((a, b) => b[1].grossCents - a[1].grossCents)
                .map(([brand, info]) => (
                  <tr key={brand}>
                    <td>{brand}</td>
                    <td className="num">{info.count}</td>
                    <td className="num">
                      <Money value={info.grossCents / 100} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      <LastUpdated dataUpdatedAt={dataUpdatedAt} />
    </div>
  );
}

function TenderBar({
  label,
  count,
  pct,
  gross,
  color,
}: {
  label: string;
  count: number;
  pct: number;
  gross: number;
  color: string;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
        <span>{label}</span>
        <span style={{ color: 'var(--fg-3)' }}>
          {count} · <Money value={gross / 100} />
        </span>
      </div>
      <div
        style={{
          marginTop: 4,
          height: 6,
          background: 'var(--bg-surface-2)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: color }} />
      </div>
    </div>
  );
}
